import { PayoutMethodEnum } from "enum/common";
import { ReactComponent as PaypalPreviewIcon } from "assets/PayPalPreview.svg";
import { ReactComponent as VenmoPreviewIcon } from "assets/VenmoPreview.svg";
import { ReactComponent as CheckPreviewIcon } from "assets/CheckPreview.svg";
import { ReactComponent as EFTPreviewIcon } from "assets/EFTPreview.svg";
import { ReactComponent as ZellePreviewIcon } from "assets/ZellePreview.svg";
import { PayoutMethodType } from "containers/PayoutManagement/types";
import ReactPopover from "components/Common/Popover";
import PayoutPopoverOptions from "components/PayoutManagement/PayoutPopoverOptions";
import { useMemo, useState } from "react";
import { MoreVertical20Filled } from "@fluentui/react-icons";
import ZellePreview from "components/PayoutManagement/previews/ZellePreview";
import VenmoPreview from "components/PayoutManagement/previews/VenmoPreview";
import CheckPreview from "components/PayoutManagement/previews/CheckPreview";
import toast from "react-hot-toast";
import { http } from "middleware/apiMiddleware";
import { deletePayoutMethod } from "constants/apiUrls";
import { useAppDispatch, useAppSelector } from "hooks/useRedux";
import { RootState } from "store";
import CanadianEFTPreview from "components/PayoutManagement/previews/CanadianEFTPreview";
import { setPayoutMethods } from "store/slices/payoutSlice";
import { Loader } from "components/Common/Loader";
import Render from "components/Common/Render";
import ConfirmationModal from "components/Common/ConfirmationModal";
import DeletePayoutConfirmation from "components/PayoutManagement/forms/DeletePayoutConfirmation";

const PayoutMethodPreview: React.FC<{
  loading: boolean;
  defaultPayoutMethod: PayoutMethodType | null;
  setPaymentModal: Function;
  setIsBankForm: (value: boolean) => void;
  afterSubmit: () => void;
  showPopooverOptions?: boolean;
}> = ({
  defaultPayoutMethod,
  setPaymentModal,
  setIsBankForm,
  afterSubmit,
  loading,
  showPopooverOptions = true,
}) => {
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [deletingPayoutMethod, setDeletingPayoutMethod] = useState(false);
  const selectedBusiness = useAppSelector(
    (state: RootState) => state.businesses.business
  );

  const onClose = () => setOpen(false);
  const onHideDeleteConfirmationModal = () => setShowDeleteConfirmation(false);

  const getPayoutIcons = (type?: PayoutMethodEnum) => {
    switch (type) {
      case PayoutMethodEnum.PAYPAL:
        return <PaypalPreviewIcon width={32} height={32} />;
      case PayoutMethodEnum.ZELLE:
        return <ZellePreviewIcon width={32} height={32} />;
      case PayoutMethodEnum.VENMO:
        return <VenmoPreviewIcon width={32} height={32} />;
      case PayoutMethodEnum.CHECK:
        return <CheckPreviewIcon width={32} height={32} />;
      case PayoutMethodEnum.CANADA_EFT:
        return <EFTPreviewIcon width={32} height={32} />;
      case PayoutMethodEnum.DEFAULT:
        return null;
    }
  };

  const payoutMethodPreview = useMemo(() => {
    switch (defaultPayoutMethod?.type) {
      case PayoutMethodEnum.PAYPAL:
      case PayoutMethodEnum.ZELLE:
        return <ZellePreview defaultPayoutMethod={defaultPayoutMethod} />;
      case PayoutMethodEnum.VENMO:
        return <VenmoPreview defaultPayoutMethod={defaultPayoutMethod} />;
      case PayoutMethodEnum.CHECK:
        return <CheckPreview defaultPayoutMethod={defaultPayoutMethod} />;
      case PayoutMethodEnum.CANADA_EFT:
        return <CanadianEFTPreview defaultPayoutMethod={defaultPayoutMethod} />;
      default:
        return <>Type doesn't match</>;
    }
  }, [defaultPayoutMethod]);

  const handleEdit = () => {
    setIsBankForm(false);
    setOpen(false);
    setPaymentModal({
      type: "edit",
      method: defaultPayoutMethod?.type,
      data: defaultPayoutMethod,
    });
  };

  const handleDelete = () => {
    setShowDeleteConfirmation(true);
  };

  const onDelete = async () => {
    try {
      setOpen(false);
      setDeletingPayoutMethod(true);
      const { data, error } = await http.delete(
        deletePayoutMethod(selectedBusiness?.uuid || "")
      );

      if (!!data && !error) {
        dispatch(setPayoutMethods(null));
        afterSubmit();
        toast("Payout method deleted successfully");
      } else {
        toast.error(
          error?.error || "Error occured while deleting payout method"
        );
      }
    } catch (error) {
      toast("Unexpected error occusred while deleting payout method");
    } finally {
      setDeletingPayoutMethod(false);
      onHideDeleteConfirmationModal();
    }
  };

  const handleChange = () => {
    setOpen(false);
    setPaymentModal({ data: null, type: "add" });
  };

  if (loading) {
    return <Loader size={6} />;
  }

  return (
    <div className="flex flex-row bg-white w-full gap-4">
      <div className="self-start md:flex-row flex gap-4 flex-col flex-1">
        <div className="md:self-center">
          {getPayoutIcons(defaultPayoutMethod?.type)}
        </div>
        <div className="flex-1 bg-grey-300 flex flex-col w-full self-center">
          <div className="grid md:grid-cols-2 gap-2">{payoutMethodPreview}</div>
        </div>
      </div>
      <Render when={showDeleteConfirmation}>
        <ConfirmationModal
          loading={deletingPayoutMethod}
          show={showDeleteConfirmation}
          onConfirmClick={onDelete}
          onHide={onHideDeleteConfirmationModal}
          renderConfirmationMessage={<DeletePayoutConfirmation />}
          yesButtonLabel="Yes, Delete"
          noButtonLabel="No, Cancel"
          customClass="md:w-[30vw] w-[70vw] !md:m-0 !p-0 !z-50 h-auto"
        />
      </Render>
      <Render when={showPopooverOptions}>
        <div className="self-start relative">
          <ReactPopover
            contentClass={"md:left-[-180px] md:w-[80px]"}
            onClose={onClose}
            open={open}
            content={
              <PayoutPopoverOptions
                setOpen={setOpen}
                onClose={onClose}
                handleEdit={handleEdit}
                handleDelete={handleDelete}
                handleChange={handleChange}
              />
            }
          >
            <div
              className="flex gap-2 md:pr-8 items-end"
              onClick={() => setOpen(true)}
            >
              <MoreVertical20Filled
                fontSize={48}
                className="m-auto self-end"
                fill="white"
              />
            </div>
          </ReactPopover>
        </div>
      </Render>
    </div>
  );
};

export default PayoutMethodPreview;
